import React from 'react';
import { IonPage, IonContent, IonIcon, IonImg } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { businessOutline, peopleOutline, personOutline } from 'ionicons/icons';
import { UserType } from '../types/user';
import {
  Typography,
  Container,
  Card,
  CardContent,
  Grid,
  Button,
  Box,
  Popover,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const Register: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const handleUserTypeSelect = (type: UserType) => {
    history.push(`/register/${type}`);
  };

  const handleInfoClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleInfoClose = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <IonPage>
      <IonContent className="ion-padding">
        <Button
          variant="text"
          startIcon={<ArrowBackIcon sx={{ color: '#16BE6B' }} />}
          onClick={() => history.push('/auth')}
          sx={{ 
            position: 'absolute',
            top: 20,
            left: 20,
            fontFamily: 'Poppins, sans-serif',
            color: '#16BE6B',
            fontSize: '1rem',
            fontWeight: 500
          }}
        >
          {t('common.back')}
        </Button>

        <Container maxWidth="sm" sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <IonImg
              src="/assets/image/logo.png"
              alt={t('logo_alt')}
              style={{
                width: '120px',
                height: '120px',
                marginBottom: '2rem'
              }}
            />

            <Typography 
              variant="h5" 
              className="animated-gradient-text" 
              align="center" 
              gutterBottom 
              sx={{ 
                mb: 4,
                fontFamily: 'Poppins, sans-serif'
              }}
            >
              {t('auth.userType')}
            </Typography>

            <Grid container spacing={3} className="stagger-children">
              <Grid item xs={12}>
                <Card 
                  className="glass-effect"
                  onClick={() => handleUserTypeSelect('company')}
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'all 0.3s ease-in-out',
                    background: 'linear-gradient(45deg, rgba(22, 190, 107, 0.05), rgba(33, 150, 243, 0.05))',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      background: 'linear-gradient(45deg, rgba(22, 190, 107, 0.1), rgba(33, 150, 243, 0.1))',
                    }
                  }}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                    <IonIcon
                      icon={businessOutline}
                      style={{ 
                        fontSize: '2rem', 
                        marginRight: '16px',
                        color: '#16BE6B'
                      }}
                    />
                    <Typography variant="h6" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                      {t('auth.company')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card 
                  className="glass-effect"
                  onClick={() => handleUserTypeSelect('contractor')}
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'all 0.3s ease-in-out',
                    background: 'linear-gradient(45deg, rgba(22, 190, 107, 0.05), rgba(33, 150, 243, 0.05))',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      background: 'linear-gradient(45deg, rgba(22, 190, 107, 0.1), rgba(33, 150, 243, 0.1))',
                    }
                  }}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                    <IonIcon
                      icon={peopleOutline}
                      style={{ 
                        fontSize: '2rem', 
                        marginRight: '16px',
                        color: '#16BE6B'
                      }}
                    />
                    <Typography variant="h6" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                      {t('auth.contractor')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card 
                  className="glass-effect"
                  onClick={() => handleUserTypeSelect('ambassador')}
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'all 0.3s ease-in-out',
                    background: 'linear-gradient(45deg, rgba(22, 190, 107, 0.05), rgba(33, 150, 243, 0.05))',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      background: 'linear-gradient(45deg, rgba(22, 190, 107, 0.1), rgba(33, 150, 243, 0.1))',
                    }
                  }}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IonIcon
                        icon={personOutline}
                        style={{ 
                          fontSize: '2rem', 
                          marginRight: '16px',
                          color: '#16BE6B'
                        }}
                      />
                      <Typography variant="h6" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                        {t('auth.ambassador')}
                      </Typography>
                    </Box>
                    <Box 
                      component="span"
                      onClick={handleInfoClick}
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'help'
                      }}
                    >
                      <InfoIcon 
                        sx={{ 
                          fontSize: '1.2rem',
                          color: '#16BE6B'
                        }} 
                      />
                    </Box>
                    <Popover
                      open={open}
                      anchorEl={anchorEl}
                      onClose={handleInfoClose}
                      onClick={(e) => e.stopPropagation()}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                      }}
                    >
                      <Typography sx={{ p: 2, maxWidth: 300, fontFamily: 'Poppins, sans-serif' }}>
                        {t('auth.ambassador_description')}
                      </Typography>
                    </Popover>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </IonContent>
    </IonPage>
  );
};

export default Register;
